import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, Inbox, Search, Mail, Trash2, Tag, X, Loader2, Send,
  CheckCircle2, Clock, Archive, Sparkles, RefreshCw, Handshake, ExternalLink, StickyNote,
} from "lucide-react";

/**
 * Sponsorship enquiries inbox — admin view of every submission received
 * via the public /sponsor fillable form. Rows are stored in the
 * ``sponsorship_submissions`` collection and served by
 * ``GET /api/sponsorship-submissions``. Admin can tag, add private
 * notes, transition status (new → contacted → converted / archived)
 * and reply via the visitor's captured email (opens the admin's own
 * mail client via ``mailto:``, matching the club's chosen delivery
 * model — no SMTP keys required).
 */

const STATUS_META = {
  new:       { label: "New",       icon: Sparkles,     color: "bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300" },
  contacted: { label: "Contacted", icon: Clock,        color: "bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300" },
  converted: { label: "Converted", icon: CheckCircle2, color: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300" },
  archived:  { label: "Archived",  icon: Archive,      color: "bg-muted text-muted-foreground border-border" },
};
const FILTER_ORDER = ["all", "new", "contacted", "converted", "archived"];

export default function AdminSponsorSubmissions() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);   // currently opened submission in the sheet

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sponsorship-submissions", { params: { status: filter } });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load submissions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Client-side search across org / contact / email / message.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((r) => [
      r.organisation, r.contact_name, r.email, r.phone, r.message,
      ...(r.tags || []), ...(r.selected_options || []),
    ].some((v) => (v || "").toString().toLowerCase().includes(needle)));
  }, [items, q]);

  const patch = async (id, body) => {
    try {
      const { data } = await api.patch(`/sponsorship-submissions/${id}`, body);
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, ...data } : r)));
      if (active?.id === id) setActive((prev) => ({ ...prev, ...data }));
      // Refresh counts (status may have moved)
      load();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save change");
      return null;
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/sponsorship-submissions/${id}`);
      setItems((prev) => prev.filter((r) => r.id !== id));
      setActive(null);
      load();
      toast.success("Submission deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <AppShell variant="admin">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="admin-submissions-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/a/sponsors" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:underline">
              <ChevronLeft className="h-3 w-3" /> Back to sponsor partners
            </Link>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1 flex items-center gap-2">
              <Inbox className="h-7 w-7 text-[hsl(var(--accent))]" /> Sponsorship enquiries
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every enquiry sent via the public sponsorship form. Tap a row to view, tag, reply or archive.
            </p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="submissions-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {FILTER_ORDER.map((k) => {
            const on = filter === k;
            const label = k === "all" ? "All" : STATUS_META[k].label;
            const n = counts[k] || 0;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  on
                    ? "bg-[hsl(var(--fg))] text-[hsl(var(--bg))] border-transparent"
                    : "bg-background text-foreground border-border hover:bg-[hsl(var(--secondary))]/60"
                }`}
                data-testid={`submissions-filter-${k}`}
              >
                {label} <span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] ${on ? "bg-white/20" : "bg-[hsl(var(--secondary))]"}`}>{n}</span>
              </button>
            );
          })}
          <div className="flex-1 min-w-[220px] max-w-md ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search org, contact, email…"
              className="pl-9 h-9 rounded-full"
              data-testid="submissions-search"
            />
          </div>
        </div>

        {/* List */}
        <Card className="rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground text-sm flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading enquiries…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground border-dashed" data-testid="submissions-empty">
              <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <div className="text-sm">
                {q ? "No enquiries match your search." : filter === "all" ? "No enquiries yet." : `No ${STATUS_META[filter]?.label.toLowerCase() || filter} enquiries.`}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((r) => (
                <SubmissionRow key={r.id} row={r} onOpen={() => setActive(r)} onDelete={remove} />
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Detail sheet */}
      <SubmissionSheet
        row={active}
        onClose={() => setActive(null)}
        onPatch={patch}
        onDelete={remove}
      />
    </AppShell>
  );
}

function SubmissionRow({ row, onOpen, onDelete }) {
  const status = row.status || "new";
  const meta = STATUS_META[status] || STATUS_META.new;
  const StatusIcon = meta.icon;
  const dt = row.created_at ? new Date(row.created_at) : null;
  return (
    <li
      className="p-4 sm:p-5 hover:bg-[hsl(var(--secondary))]/40 cursor-pointer transition-colors"
      onClick={onOpen}
      data-testid={`submission-row-${row.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-sm sm:text-base truncate">
              {row.organisation || row.contact_name || "(no name)"}
            </div>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${meta.color}`}>
              <StatusIcon className="h-3 w-3" /> {meta.label}
            </span>
            {(row.tags || []).slice(0, 3).map((t, i) => (
              <span key={`${t}-${i}`} className="inline-flex items-center gap-1 text-[10px] rounded-full bg-[hsl(var(--secondary))] px-2 py-0.5">
                <Tag className="h-2.5 w-2.5" /> {t}
              </span>
            ))}
            {row.tags?.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{row.tags.length - 3}</span>
            )}
            {row.sponsor_id && (
              <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 px-2 py-0.5" data-testid={`submission-linked-sponsor-${row.id}`}>
                <Handshake className="h-2.5 w-2.5" /> Linked sponsor
              </span>
            )}
            {row.admin_notes && (
              <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 px-2 py-0.5" data-testid={`submission-has-notes-${row.id}`}>
                <StickyNote className="h-2.5 w-2.5" /> Notes
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            {row.contact_name && row.organisation && <span>{row.contact_name}</span>}
            {row.email && <span>· {row.email}</span>}
            {row.phone && <span>· {row.phone}</span>}
            {dt && <span>· {dt.toLocaleString()}</span>}
          </div>
          {(row.selected_options || []).length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {row.selected_options.map((o, i) => (
                <Badge key={`${o}-${i}`} variant="outline" className="text-[10px] rounded-full">{o}</Badge>
              ))}
            </div>
          )}
          {row.message && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{row.message}</p>
          )}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 mt-0.5 rounded-full p-2 text-muted-foreground hover:text-red-600 hover:bg-red-500/10 transition-colors"
              aria-label="Remove enquiry"
              data-testid={`submission-row-delete-${row.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove this enquiry?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the enquiry from {row.organisation || row.contact_name || "this sender"} from the database. This can't be undone.
                {row.sponsor_id && " The linked sponsor card will remain — delete it separately from the Sponsors page if needed."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={() => onDelete(row.id)}
                data-testid={`submission-row-delete-confirm-${row.id}`}
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  );
}

function SubmissionSheet({ row, onClose, onPatch, onDelete }) {
  const [tagInput, setTagInput] = useState("");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    setNotes(row?.admin_notes || "");
    setTagInput("");
  }, [row?.id]);

  if (!row) {
    return (
      <Sheet open={false} onOpenChange={onClose}>
        <SheetContent side="right" className="w-full sm:max-w-lg" />
      </Sheet>
    );
  }

  const status = row.status || "new";
  const dt = row.created_at ? new Date(row.created_at) : null;

  const addTag = async () => {
    const v = tagInput.trim();
    if (!v) return;
    const next = Array.from(new Set([...(row.tags || []), v]));
    await onPatch(row.id, { tags: next });
    setTagInput("");
  };

  const removeTag = async (t) => {
    const next = (row.tags || []).filter((x) => x !== t);
    await onPatch(row.id, { tags: next });
  };

  const changeStatus = async (v) => {
    await onPatch(row.id, { status: v });
    toast.success(`Marked as ${STATUS_META[v]?.label.toLowerCase()}`);
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    await onPatch(row.id, { admin_notes: notes });
    setSavingNotes(false);
    toast.success("Notes saved");
  };

  const reply = () => {
    if (!row.email) {
      toast.error("No email captured on this enquiry — reply by phone instead");
      return;
    }
    const subject = `Re: Sponsorship enquiry — ${row.organisation || row.contact_name || "Derwent Cricket Club"}`;
    const summary = [
      "",
      "",
      "----- Original enquiry -----",
      `From: ${row.contact_name || ""} (${row.organisation || ""})`,
      `Selected: ${(row.selected_options || []).join(", ") || "-"}`,
      `Message:  ${row.message || "-"}`,
    ].join("\n");
    const body = `Hi ${row.contact_name || "there"},\n\nThanks for your interest in sponsoring the Derwent Cricket Club.${summary}`;
    const href = `mailto:${row.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(href, "_self");
    // Auto-transition to Contacted on first reply
    if (status === "new") onPatch(row.id, { status: "contacted" });
  };

  const convertToSponsor = async () => {
    setConverting(true);
    try {
      const { data } = await api.post(`/sponsorship-submissions/${row.id}/convert`);
      const sponsor = data?.sponsor;
      const already = data?.already_converted;
      if (!sponsor) {
        toast.error("Convert failed — no sponsor returned");
        return;
      }
      // Reflect the linkage locally so the button flips to "View sponsor"
      // without needing a full inbox refetch first.
      const patched = { ...row, sponsor_id: sponsor.id, status: "converted" };
      if (onPatch) {
        // Push into cache — API-level status was already changed server-side.
        await onPatch(row.id, { status: "converted" });
      }
      toast.success(
        already
          ? `Already promoted — opening ${sponsor.name}`
          : `Sponsor "${sponsor.name}" created — finish adding logo + website`,
      );
      // Hand off to the Sponsors page so the admin can attach a logo
      // and flip the sponsor active immediately. The Sponsors page
      // scrolls to & auto-opens the row matching `?edit=<id>`.
      window.location.href = `/a/sponsors?edit=${sponsor.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not convert enquiry");
    } finally {
      setConverting(false);
    }
  };

  return (
    <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="submission-sheet">
        <SheetHeader>
          <SheetTitle className="text-xl font-bold flex items-center gap-2 pr-6">
            {row.organisation || row.contact_name || "Enquiry"}
          </SheetTitle>
          <SheetDescription>
            {dt ? dt.toLocaleString() : ""} · Enquiry ID {(row.id || "").slice(0, 8)}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Status */}
          <div>
            <Label className="text-xs font-medium">Status</Label>
            <Select value={status} onValueChange={changeStatus}>
              <SelectTrigger className="mt-1 h-10 rounded-lg" data-testid="submission-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>
                    <span className="inline-flex items-center gap-2">
                      <m.icon className="h-3.5 w-3.5" /> {m.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact block */}
          <Card className="p-4 rounded-xl space-y-1.5 text-sm">
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Organisation</span><span className="font-semibold text-right">{row.organisation || "-"}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Contact</span><span className="font-semibold text-right">{row.contact_name || "-"}</span></div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Email</span>
              <span className="font-semibold text-right break-all">
                {row.email
                  ? <a href={`mailto:${row.email}`} className="underline">{row.email}</a>
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Phone</span>
              <span className="font-semibold text-right">
                {row.phone
                  ? <a href={`tel:${row.phone}`} className="underline">{row.phone}</a>
                  : "-"}
              </span>
            </div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">Monetary</span><span className="font-semibold">{row.monetary ? "Yes" : "No"}</span></div>
            <div className="flex justify-between gap-3"><span className="text-muted-foreground">In-kind</span><span className="font-semibold">{row.in_kind ? "Yes" : "No"}</span></div>
            {row.in_kind_details && (
              <div className="pt-2 border-t border-border">
                <div className="text-muted-foreground text-xs mb-1">In-kind details</div>
                <div className="whitespace-pre-line">{row.in_kind_details}</div>
              </div>
            )}
          </Card>

          {/* Selected packages */}
          {(row.selected_options || []).length > 0 && (
            <div>
              <Label className="text-xs font-medium">Selected packages</Label>
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                {row.selected_options.map((o, i) => (
                  <Badge key={`${o}-${i}`} variant="outline" className="rounded-full">{o}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          {row.message && (
            <div>
              <Label className="text-xs font-medium">Message from the sender</Label>
              <div className="mt-1 rounded-xl bg-[hsl(var(--secondary))]/50 p-3 text-sm whitespace-pre-line">
                {row.message}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <Label className="text-xs font-medium">Tags</Label>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {(row.tags || []).map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--secondary))] px-2.5 py-1 text-xs"
                  data-testid={`submission-tag-${t}`}
                >
                  <Tag className="h-3 w-3" /> {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="ml-0.5 rounded-full hover:bg-foreground/10 h-4 w-4 flex items-center justify-center"
                    aria-label={`Remove tag ${t}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              {(row.tags || []).length === 0 && (
                <span className="text-xs text-muted-foreground italic">No tags yet</span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add a tag (e.g. gold-prospect)"
                className="h-9 rounded-full text-sm"
                maxLength={40}
                data-testid="submission-tag-input"
              />
              <Button size="sm" variant="outline" className="rounded-full h-9" onClick={addTag} data-testid="submission-tag-add">
                Add
              </Button>
            </div>
          </div>

          {/* Admin notes */}
          <div>
            <Label className="text-xs font-medium">Private admin notes</Label>
            <Textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes visible only to admins (e.g. call outcomes, follow-up dates)"
              className="mt-1 rounded-lg"
              data-testid="submission-notes-input"
            />
            <div className="flex justify-end mt-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full h-8"
                onClick={saveNotes}
                disabled={savingNotes || notes === (row.admin_notes || "")}
                data-testid="submission-notes-save"
              >
                {savingNotes ? "Saving…" : "Save notes"}
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-2 border-t border-border space-y-2">
            {row.sponsor_id ? (
              <a
                href={`/a/sponsors?edit=${row.sponsor_id}`}
                className="block w-full"
                data-testid="submission-view-sponsor-btn"
              >
                <Button className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="h-4 w-4 mr-2" /> View linked sponsor
                  <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-70" />
                </Button>
              </a>
            ) : (
              <Button
                className="w-full rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] hover:bg-[hsl(var(--fg))]/90"
                onClick={convertToSponsor}
                disabled={converting}
                data-testid="submission-convert-btn"
              >
                {converting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating sponsor…</>
                ) : (
                  <><Handshake className="h-4 w-4 mr-2" /> Convert to sponsor</>
                )}
              </Button>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                className="rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90 flex-1 min-w-[8rem]"
                onClick={reply}
                data-testid="submission-reply-btn"
              >
                <Send className="h-4 w-4 mr-2" /> Reply via email
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="rounded-full text-red-600 hover:text-red-700" data-testid="submission-delete-btn">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this enquiry?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the enquiry from the database. This can't be undone.
                      {row.sponsor_id && " The linked sponsor card will remain — delete it separately from the Sponsors page if you no longer want it."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-red-600 hover:bg-red-700"
                      onClick={() => onDelete(row.id)}
                      data-testid="submission-delete-confirm"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
